import React, { useState, useEffect } from 'react';

const EncryptionTest = () => {
  const [plainText, setPlainText] = useState('');
  const [encryptedPayload, setEncryptedPayload] = useState(null);
  const [decryptedText, setDecryptedText] = useState('');
  const [keys, setKeys] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [decrypting, setDecrypting ] = useState(false);
  const [error, setError] = useState('');

  const apiUrl = 'http://localhost:5000/api/encryption';

  const fetchData = async (url, method = 'GET', body = null) => {
  setLoading(true);
  setError('');
  try {
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : null
    };
    
    const response = await fetch(`${apiUrl}${url}`, options);
    
    // First get the response text
    const responseText = await response.text();
    
    // Try to parse as JSON, fall back to text if it fails
    let data;
    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch (e) {
      data = responseText;
    }
    
    if (!response.ok) {
      // If we have JSON error data, use it
      if (typeof data === 'object' && data.error) {
        throw new Error(data.error);
      }
      // Otherwise use status text or raw response
      throw new Error(response.statusText || responseText || 'Request failed');
    }
    
    return data;
  } catch (err) {
    setError(err.message || 'Something went wrong');
    throw err;
  } finally {
    setLoading(false);
  }
};

const handleDecrypt = async () => {
  if (!encryptedPayload) {
    setError('No encrypted payload provided');
    return;
  }

  try {
    setDecrypting(true);
    setError('');
    
    const data = await fetchData('/decrypt', 'POST', encryptedPayload);
    
    // Handle different response formats
    if (typeof data === 'object') {
      setDecryptedText(data.DecryptedData || data.decryptedData || data.data || "Decryption successful");
    } else if (typeof data === 'string') {
      setDecryptedText(data);
    } else {
      setDecryptedText("Decryption completed (no data returned)");
    }
  } catch (err) {
    console.error("Decryption failed:", err);
    setError(err.message || 'Decryption failed');
  } finally {
    setDecrypting(false);
  }
};

  const handleEncrypt = async () => {
    const data = await fetchData('/encrypt', 'POST', plainText);
    console.log("davinchi", data);
    setEncryptedPayload(data);
    setDecryptedText('');
  };

 

  const handleGenerateKey = async () => {
    await fetchData('/keys/generate', 'POST');
    loadKeys();
  };

  const handleRotateKeys = async () => {
    await fetchData('/keys/rotate', 'POST');
    loadKeys();
    loadActiveKey();
  };

  const handleDisableKey = async (keyId) => {
    await fetchData(`/keys/${keyId}/disable`, 'POST');
    loadKeys();
    loadActiveKey();
  };

  const loadKeys = async () => {
    const data = await fetchData('/keys/active');
    setKeys([data]);
    setActiveKey(data);
  };

  const loadActiveKey = async () => {
    const data = await fetchData('/keys/active');
    setActiveKey(data);
  };

  useEffect(() => {
    loadKeys();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Encryption Service</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Encryption Section */}
          <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-semibold mb-2">Encrypt</h2>
            <textarea
              className="w-full p-2 border rounded mb-2"
              rows={4}
              value={plainText}
              onChange={(e) => setPlainText(e.target.value)}
              placeholder="Enter text to encrypt"
            />
            <button
              onClick={handleEncrypt}
              disabled={loading || !plainText.trim()}
              className={`bg-blue-500 text-white px-4 py-2 rounded ${loading || !plainText.trim() ? 'opacity-50' : 'hover:bg-blue-600'}`}
            >
              {loading ? 'Processing...' : 'Encrypt'}
            </button>
          </div>

          {/* Decryption Section */}
          <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-semibold mb-2">Decrypt</h2>
            <button
              onClick={handleDecrypt}
              disabled={loading || !encryptedPayload}
              className={`bg-green-500 text-white px-4 py-2 rounded mb-2 ${loading || !encryptedPayload ? 'opacity-50' : 'hover:bg-green-600'}`}
            >
              {loading ? 'Processing...' : 'Decrypt'}
            </button>
            {decryptedText && (
              <div className="mt-2 p-2 bg-gray-50 rounded border">
                <p className="whitespace-pre-wrap">{decryptedText}</p>
              </div>
            )}
          </div>
        </div>

        {/* Encrypted Data Display */}
        {encryptedPayload && (
          <div className="mt-4 bg-white p-4 rounded shadow">
            <h2 className="text-lg font-semibold mb-2">Encrypted Result</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <p className="text-sm text-gray-500">Data</p>
                <p className="text-xs break-all bg-gray-50 p-2 rounded">{encryptedPayload.encryptedData}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Key</p>
                <p className="text-xs break-all bg-gray-50 p-2 rounded">{encryptedPayload.encryptedKey}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">IV</p>
                <p className="text-xs break-all bg-gray-50 p-2 rounded">{encryptedPayload.iv}</p>
              </div>
            </div>
          </div>
        )}

        {/* Key Management */}
        <div className="mt-6 bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Key Management</h2>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateKey}
                disabled={loading}
                className={`bg-purple-500 text-white px-3 py-1 rounded text-sm ${loading ? 'opacity-50' : 'hover:bg-purple-600'}`}
              >
                New Key
              </button>
              <button
                onClick={handleRotateKeys}
                disabled={loading}
                className={`bg-yellow-500 text-white px-3 py-1 rounded text-sm ${loading ? 'opacity-50' : 'hover:bg-yellow-600'}`}
              >
                Rotate
              </button>
            </div>
          </div>

          {activeKey && (
            <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
              <h3 className="font-medium mb-2">Active Key</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>
                  <p className="text-gray-500">Name</p>
                  <p>{activeKey.keyName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expires</p>
                  <p>{new Date(activeKey.expirationDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs ${activeKey.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {activeKey.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2 text-sm">ID</th>
                  <th className="p-2 text-sm">Name</th>
                  <th className="p-2 text-sm">Expires</th>
                  <th className="p-2 text-sm">Status</th>
                  <th className="p-2 text-sm">Action</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.Id} className="border-b">
                    <td className="p-2 text-xs break-all">{key.Id}</td>
                    <td className="p-2">{key.keyName}</td>
                    <td className="p-2">{new Date(key.expirationDate).toLocaleDateString()}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${key.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {key.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => handleDisableKey(key.id)}
                        disabled={loading || !key.isActive}
                        className={`text-xs px-2 py-1 rounded ${loading || !key.isActive ? 'bg-gray-100 text-gray-400' : 'bg-red-500 text-white hover:bg-red-600'}`}
                      >
                        Disable
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncryptionTest;